import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { eq } from 'ember-truth-helpers';
import type Owner from '@ember/owner';
import type { Store } from '@warp-drive/core';
import type Notifications from '../services/notifications';
import type Modals from '../services/modals';
import type RouterService from '@ember/routing/router-service';
import type { Tag } from '../schemas/tag.ts';
import { getTags, createTag, deleteTag } from '../builders/profile';
import TagIconLucide from '~icons/lucide/tag';
import TrashIcon from '~icons/lucide/trash-2';
import PlusIcon from '~icons/lucide/plus';
import Icon from './icon';
import pullToRefresh from '../modifiers/pull-to-refresh.ts';

interface CollectionsListSignature {
  Element: HTMLDivElement;
}

export default class CollectionsList extends Component<CollectionsListSignature> {
  @service declare store: Store;
  @service declare notifications: Notifications;
  @service declare modals: Modals;
  @service declare router: RouterService;

  @tracked tags: Tag[] = [];
  @tracked isLoading = true;
  @tracked newTagLabel = '';
  @tracked isCreating = false;

  constructor(owner: Owner, args: object) {
    super(owner, args);
    void this.loadTags();
  }

  @action
  async loadTags() {
    this.isLoading = true;
    try {
      // Fresh fetch so pull-to-refresh and post-mutation reloads bypass the cache
      const result = await this.store.request({
        ...getTags(),
        cacheOptions: { reload: true },
      });
      const json = result.content as {
        data?: Tag[];
      };
      this.tags = json.data ?? [];
    } catch {
      this.notifications.error('Failed to load tags');
    } finally {
      this.isLoading = false;
    }
  }

  @action
  updateNewTag(evt: Event) {
    this.newTagLabel = (evt.target as HTMLInputElement).value;
  }

  @action
  async handleCreateTag(evt?: Event) {
    evt?.preventDefault();
    const label = this.newTagLabel.trim();
    if (!label || this.isCreating) return;

    this.isCreating = true;
    try {
      await this.store.request(createTag(label));
      this.newTagLabel = '';
      this.notifications.success(`Tag "${label}" created`);
      void this.loadTags();
    } catch {
      this.notifications.error('Failed to create tag');
    } finally {
      this.isCreating = false;
    }
  }

  @action
  async handleDeleteTag(tag: Tag, evt?: Event) {
    evt?.stopPropagation();
    const confirmed = await this.modals.confirm({
      title: 'Delete tag',
      message: `Delete tag "${tag.label}"? It will be removed from all items.`,
      confirmText: 'Delete',
    });
    if (!confirmed) return;

    try {
      await this.store.request(deleteTag(tag.id!));
      this.notifications.success(`Tag "${tag.label}" deleted`);
      void this.loadTags();
    } catch {
      this.notifications.error('Failed to delete tag');
    }
  }

  @action
  goToTagItems() {
    // TODO: pass tagId as queryParam when controller added
    void this.router.transitionTo('items');
  }

  <template>
    <div ...attributes {{pullToRefresh this.loadTags}}>
      {{! Create new tag }}
      <form class="mb-6 flex gap-2" {{on "submit" this.handleCreateTag}}>
        <input
          type="text"
          class="input input-bordered input-sm flex-1"
          placeholder="New tag name…"
          aria-label="New tag name"
          value={{this.newTagLabel}}
          {{on "input" this.updateNewTag}}
        />
        <button
          type="submit"
          class="btn btn-primary btn-sm gap-1"
          disabled={{this.isCreating}}
        >
          <Icon @icon={{PlusIcon}} class="h-4 w-4" />
          Add
        </button>
      </form>

      {{#if this.isLoading}}
        <div class="flex justify-center py-12">
          <span class="loading loading-spinner loading-lg"></span>
        </div>
      {{else if this.tags.length}}
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {{#each this.tags as |tag|}}
            <div
              class="card bg-base-100 shadow-md transition-shadow hover:shadow-lg"
            >
              <div class="card-body p-4">
                <div class="flex items-center gap-2">
                  <Icon
                    @icon={{TagIconLucide}}
                    class="text-primary h-5 w-5"
                    aria-hidden="true"
                  />
                  <button
                    type="button"
                    class="card-title flex-1 cursor-pointer text-base hover:underline"
                    {{on "click" (fn this.goToTagItems tag)}}
                  >
                    {{tag.label}}
                  </button>
                  <button
                    type="button"
                    class="btn btn-ghost btn-xs"
                    title="Delete tag"
                    {{on "click" (fn this.handleDeleteTag tag)}}
                  >
                    <Icon
                      @icon={{TrashIcon}}
                      class="h-3.5 w-3.5"
                      aria-hidden="true"
                    />
                  </button>
                </div>
                <p class="text-base-content/60 text-sm">
                  {{tag.itemCount}}
                  {{if (eq tag.itemCount 1) "item" "items"}}
                </p>
              </div>
            </div>
          {{/each}}
        </div>
      {{else}}
        <div class="text-base-content/60 py-12 text-center">
          No collections yet. Create a tag above to organize your items.
        </div>
      {{/if}}
    </div>
  </template>
}
